<template>
  <PartnersLayout>
    <template #header>
      <PartnersHeader
        title="스플래시 광고 신청"
        subtitle="앱 시작 시 팝업/바텀시트로 노출되는 광고입니다"
      />
    </template>

    <div class="px-4 lg:px-6 pb-6">
      <div class="max-w-6xl mx-auto">
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- 왼쪽: 안내 및 바로가기 -->
          <div class="lg:col-span-1 space-y-4">
            <!-- 안내 카드 -->
            <div class="bg-white rounded-2xl border border-slate-200 p-5">
              <div class="flex items-center gap-2 mb-3">
                <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <h3 class="font-semibold text-slate-800">스플래시 광고 안내</h3>
              </div>
              <ul class="text-sm text-slate-600 space-y-2">
                <li class="flex items-start gap-2">
                  <span class="text-blue-500 mt-0.5">•</span>
                  <span>광고 등록은 <strong class="text-blue-700">무료</strong>입니다</span>
                </li>
                <li class="flex items-start gap-2">
                  <span class="text-blue-500 mt-0.5">•</span>
                  <span>신청 후 <strong class="text-blue-700">관리자 승인</strong>이 필요합니다</span>
                </li>
                <li class="flex items-start gap-2">
                  <span class="text-blue-500 mt-0.5">•</span>
                  <span>광고 기간은 <strong class="text-blue-700">1주일</strong>입니다</span>
                </li>
                <li class="flex items-start gap-2">
                  <span class="text-blue-500 mt-0.5">•</span>
                  <span>계정당 <strong class="text-blue-700">1개</strong>만 등록 가능</span>
                </li>
                <li class="flex items-start gap-2">
                  <span class="text-blue-500 mt-0.5">•</span>
                  <span>광고 유입 콜은 <strong class="text-blue-700">건당 10,000P</strong>에 구매</span>
                </li>
                <li class="flex items-start gap-2">
                  <span class="text-blue-500 mt-0.5">•</span>
                  <span>앱 시작 시 <strong class="text-blue-700">팝업/시트</strong>로 노출</span>
                </li>
              </ul>
            </div>

            <!-- 바로가기 -->
            <div class="bg-white rounded-2xl border border-slate-200 p-5">
              <h3 class="font-semibold text-slate-800 mb-3">바로가기</h3>
              <div class="space-y-2">
                <router-link
                  to="/partners/splash/my"
                  class="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition group"
                >
                  <div class="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition">
                    <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                    </svg>
                  </div>
                  <div class="flex-1">
                    <p class="text-sm font-medium text-slate-700">내 스플래시 광고</p>
                    <p class="text-xs text-slate-500">신청 현황 확인</p>
                  </div>
                  <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </router-link>
                <router-link
                  to="/partners/ad/create"
                  class="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition group"
                >
                  <div class="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition">
                    <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
                    </svg>
                  </div>
                  <div class="flex-1">
                    <p class="text-sm font-medium text-slate-700">캠페인 만들기</p>
                    <p class="text-xs text-slate-500">광고에 필요한 캠페인 생성</p>
                  </div>
                  <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </router-link>
              </div>
            </div>
          </div>

          <!-- 오른쪽: 메인 콘텐츠 -->
          <div class="lg:col-span-2">
            <!-- 로딩 -->
            <div v-if="loading" class="bg-white rounded-2xl border border-slate-200 p-12 flex items-center justify-center">
              <div class="spinner"></div>
            </div>

            <!-- 캠페인 없음 -->
            <div v-else-if="campaigns.length === 0" class="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div class="flex items-center gap-3">
                <svg class="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
                <div>
                  <p class="font-semibold text-amber-800">활성화된 캠페인이 없습니다</p>
                  <p class="text-sm text-amber-600">스플래시 광고를 등록하려면 먼저 캠페인을 생성해주세요.</p>
                  <router-link to="/partners/ad/create" class="inline-flex items-center gap-1 mt-2 text-sm text-blue-600 font-medium hover:underline">
                    캠페인 만들기
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </router-link>
                </div>
              </div>
            </div>

            <!-- 이미 신청함 -->
            <div v-else-if="hasExisting" class="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div class="flex items-start gap-3">
                <svg class="w-6 h-6 text-amber-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
                <div>
                  <p class="font-semibold text-amber-800">이미 등록된 광고가 있습니다</p>
                  <p class="text-sm text-amber-600 mt-1">계정당 1개의 광고만 등록할 수 있습니다.</p>
                  <p class="text-sm text-amber-600">기존 광고가 만료되거나 삭제된 후 새로운 광고를 신청해주세요.</p>
                  <router-link
                    to="/partners/splash/my"
                    class="inline-flex items-center gap-1 mt-3 text-sm text-blue-600 font-medium hover:underline"
                  >
                    내 광고 확인하기
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </router-link>
                </div>
              </div>
            </div>

            <!-- 신청 폼 -->
            <div v-else class="space-y-4">
              <!-- 캠페인 선택 -->
              <div class="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 class="text-lg font-semibold text-slate-800 mb-4">1. 캠페인 선택</h2>

                <div class="space-y-3">
                  <label
                    v-for="campaign in campaigns"
                    :key="campaign.id"
                    :class="[
                      'flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition',
                      selectedCampaign?.id === campaign.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 hover:border-blue-300'
                    ]"
                  >
                    <input
                      type="radio"
                      :value="campaign"
                      v-model="selectedCampaign"
                      class="w-5 h-5 text-blue-600"
                    />
                    <div class="flex-1">
                      <h3 class="font-semibold text-slate-800">{{ campaign.propertyName }}</h3>
                      <p class="text-sm text-slate-500">캠페인 코드: {{ campaign.code }}</p>
                      <p class="text-xs text-slate-400 mt-1">만료일: {{ campaign.endDate }}</p>
                    </div>
                    <span class="px-3 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                      활성
                    </span>
                  </label>
                </div>
              </div>

              <!-- 시간대 선택 -->
              <div class="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 class="text-lg font-semibold text-slate-800 mb-1">2. 노출 시간대 선택</h2>
                <p class="text-sm text-slate-500 mb-4">광고가 노출될 시간대를 선택하세요. (1개만 선택 가능)</p>

                <div class="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                  <button
                    v-for="slot in TIME_SLOTS"
                    :key="slot.id"
                    type="button"
                    @click="selectedTimeSlot = slot"
                    :class="[
                      'p-3 rounded-xl border-2 text-left transition',
                      selectedTimeSlot?.id === slot.id
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500/20'
                        : 'border-slate-200 hover:border-blue-300 hover:bg-blue-50/50'
                    ]"
                  >
                    <p :class="['text-sm font-medium', selectedTimeSlot?.id === slot.id ? 'text-blue-700' : 'text-slate-700']">{{ slot.label }}</p>
                    <p :class="['text-xs mt-0.5', selectedTimeSlot?.id === slot.id ? 'text-blue-500' : 'text-slate-400']">{{ slot.start }}-{{ slot.end }}</p>
                  </button>
                </div>
              </div>

              <!-- 광고 콘텐츠 -->
              <div class="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 class="text-lg font-semibold text-slate-800 mb-4">광고 콘텐츠</h2>

                <!-- 이미지 업로드 -->
                <div class="mb-4">
                  <label class="block text-sm font-medium text-slate-700 mb-2">
                    광고 이미지 <span class="text-red-500">*</span>
                  </label>
                  <div
                    class="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:border-blue-400 transition cursor-pointer"
                    @click="triggerFileInput"
                  >
                    <input
                      ref="fileInput"
                      type="file"
                      accept="image/*"
                      class="hidden"
                      @change="handleFileSelect"
                    />
                    <div v-if="previewUrl" class="relative">
                      <img :src="previewUrl" class="max-h-60 mx-auto rounded-lg" />
                      <button
                        @click.stop="clearImage"
                        class="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                      </button>
                    </div>
                    <div v-else>
                      <svg class="w-12 h-12 text-slate-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                      </svg>
                      <p class="text-slate-500">클릭하여 이미지 업로드</p>
                      <p class="text-xs text-slate-400 mt-1">권장: 4:3 비율, 최대 5MB</p>
                    </div>
                  </div>
                </div>

                <!-- 제목 -->
                <div class="mb-4">
                  <label class="block text-sm font-medium text-slate-700 mb-2">
                    광고 제목 <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.title"
                    type="text"
                    class="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="예: 래미안 원베일리 특별분양"
                    maxlength="30"
                  />
                  <p class="text-xs text-slate-400 mt-1 text-right">{{ form.title.length }}/30</p>
                </div>

                <!-- 부제목 -->
                <div class="mb-4">
                  <label class="block text-sm font-medium text-slate-700 mb-2">
                    부제목 (선택)
                  </label>
                  <input
                    v-model="form.subtitle"
                    type="text"
                    class="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="예: 강남 핵심 입지, 프리미엄 주거단지"
                    maxlength="50"
                  />
                  <p class="text-xs text-slate-400 mt-1 text-right">{{ form.subtitle.length }}/50</p>
                </div>

                <!-- 지역 태그 -->
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">
                    지역 태그 (선택)
                  </label>
                  <input
                    v-model="form.region"
                    type="text"
                    class="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="예: 서울 강남"
                    maxlength="10"
                  />
                </div>
              </div>

              <!-- 미리보기 -->
              <div class="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 class="text-lg font-semibold text-slate-800 mb-4">미리보기</h2>
                <p class="text-sm text-slate-500 mb-4">웹에서는 팝업, 모바일에서는 바텀시트로 표시됩니다</p>

                <!-- 4:3 비율 팝업 스타일 미리보기 -->
                <div class="relative bg-white rounded-2xl shadow-xl overflow-hidden mx-auto" style="max-width: 320px;">
                  <!-- 이미지 영역 (4:3) -->
                  <div class="relative aspect-[4/3]">
                    <img
                      v-if="previewUrl"
                      :src="previewUrl"
                      class="w-full h-full object-cover"
                    />
                    <div v-else class="w-full h-full bg-gradient-to-b from-blue-600 to-blue-700 flex items-center justify-center">
                      <span class="text-5xl">🦭</span>
                    </div>

                    <!-- 그라데이션 -->
                    <div class="absolute inset-0" style="background: linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, transparent 40%, transparent 60%, rgba(0,0,0,0.4) 100%);"></div>

                    <!-- AD 배지 -->
                    <div class="absolute top-3 left-3 flex items-center gap-2">
                      <span class="px-2 py-0.5 text-xs font-bold bg-blue-600 text-white rounded">AD</span>
                      <span v-if="form.region" class="px-2 py-0.5 text-xs bg-white/90 text-slate-700 rounded">{{ form.region }}</span>
                    </div>

                    <!-- 닫기 버튼 -->
                    <span class="absolute top-3 right-3 w-7 h-7 bg-black/40 text-white rounded-full flex items-center justify-center">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                    </span>
                  </div>

                  <!-- 정보 영역 -->
                  <div class="p-4">
                    <h3 class="text-base font-bold text-slate-900 mb-1">{{ form.title || '광고 제목' }}</h3>
                    <p v-if="form.subtitle" class="text-sm text-slate-500 mb-3">{{ form.subtitle }}</p>
                    <p v-else class="text-sm text-slate-400 mb-3">부제목을 입력하세요</p>

                    <!-- CTA 버튼 -->
                    <button class="w-full py-2.5 bg-blue-600 text-white font-semibold rounded-xl flex items-center justify-center gap-1 text-sm">
                      자세히 보기
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                      </svg>
                    </button>
                  </div>

                  <!-- 카운트다운 바 -->
                  <div class="absolute bottom-0 left-0 right-0 h-1 bg-slate-200">
                    <div class="h-full w-full bg-blue-600"></div>
                  </div>
                </div>

                <p class="text-xs text-slate-400 text-center mt-3">권장 이미지 비율: 4:3</p>
              </div>

              <!-- 신청 버튼 -->
              <button
                @click="submitApplication"
                :disabled="!canSubmit || submitting"
                :class="[
                  'w-full py-4 rounded-xl font-semibold text-lg transition',
                  canSubmit && !submitting
                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                ]"
              >
                <span v-if="submitting" class="flex items-center justify-center gap-2">
                  <svg class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  신청 중...
                </span>
                <span v-else>스플래시 광고 신청하기 (무료)</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </PartnersLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { db, storage } from '@/firebase/firebase'
import { useAuth } from '@/composables/common/useAuth'
import { useSplashAd } from '@/composables/main/useSplashAd'
import PartnersLayout from '@/components/partners/common/PartnersLayout.vue'
import PartnersHeader from '@/components/partners/common/PartnersHeader.vue'

export default {
  name: 'SplashAdPurchaseView',
  components: {
    PartnersLayout,
    PartnersHeader
  },
  setup() {
    const router = useRouter()
    const { user, userProfile } = useAuth()
    const { TIME_SLOTS, applySplashAd, checkExistingSplashAd } = useSplashAd()

    const loading = ref(true)
    const submitting = ref(false)
    const campaigns = ref([])
    const selectedCampaign = ref(null)
    const hasExisting = ref(false)
    const selectedTimeSlot = ref(null)
    const selectedFile = ref(null)
    const previewUrl = ref('')
    const fileInput = ref(null)

    const form = ref({
      title: '',
      subtitle: '',
      region: ''
    })

    const canSubmit = computed(() => {
      return selectedCampaign.value && selectedTimeSlot.value && selectedFile.value && form.value.title.trim()
    })

    const loadData = async () => {
      if (!user.value) return

      try {
        loading.value = true

        // 활성 캠페인 확인
        const campaignQ = query(
          collection(db, 'campaigns'),
          where('partnerId', '==', user.value.uid),
          where('status', '==', 'active')
        )
        const campaignSnapshot = await getDocs(campaignQ)

        campaigns.value = campaignSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))

        // 캠페인이 하나면 자동 선택
        if (campaigns.value.length === 1) {
          selectedCampaign.value = campaigns.value[0]
        }

        // 기존 신청 확인
        hasExisting.value = await checkExistingSplashAd(user.value.uid)
      } catch (error) {
        console.error('데이터 로드 실패:', error)
      } finally {
        loading.value = false
      }
    }

    const triggerFileInput = () => {
      fileInput.value?.click()
    }

    const handleFileSelect = (event) => {
      const file = event.target.files[0]
      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          alert('파일 크기는 5MB 이하여야 합니다.')
          return
        }
        selectedFile.value = file
        previewUrl.value = URL.createObjectURL(file)
      }
    }

    const clearImage = () => {
      selectedFile.value = null
      previewUrl.value = ''
      if (fileInput.value) {
        fileInput.value.value = ''
      }
    }

    const submitApplication = async () => {
      if (!canSubmit.value || submitting.value) return

      try {
        submitting.value = true

        // 이미지 업로드
        const fileName = `splash_${user.value.uid}_${Date.now()}.${selectedFile.value.name.split('.').pop()}`
        const imageRef = storageRef(storage, `splash-ads/${fileName}`)
        await uploadBytes(imageRef, selectedFile.value)
        const imageUrl = await getDownloadURL(imageRef)

        // 신청 데이터
        const applicationData = {
          partnerId: user.value.uid,
          partnerEmail: user.value.email,
          partnerDisplayName: userProfile.value?.name || user.value.email,
          campaignId: selectedCampaign.value.id,
          campaignCode: selectedCampaign.value.code,
          propertyId: selectedCampaign.value.propertyId,
          propertyName: selectedCampaign.value.propertyName,
          timeSlot: selectedTimeSlot.value,
          imageUrl,
          title: form.value.title.trim(),
          subtitle: form.value.subtitle.trim(),
          region: form.value.region.trim()
        }

        await applySplashAd(applicationData)

        alert('스플래시 광고 신청이 완료되었습니다. 어드민 승인 후 노출됩니다.')
        router.push('/partners/splash/my')
      } catch (error) {
        console.error('신청 실패:', error)
        alert('신청에 실패했습니다. 다시 시도해주세요.')
      } finally {
        submitting.value = false
      }
    }

    onMounted(loadData)

    return {
      loading,
      submitting,
      campaigns,
      selectedCampaign,
      hasExisting,
      TIME_SLOTS,
      selectedTimeSlot,
      selectedFile,
      previewUrl,
      fileInput,
      form,
      canSubmit,
      triggerFileInput,
      handleFileSelect,
      clearImage,
      submitApplication
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";

.spinner {
  @apply w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin;
}
</style>
